"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.plan90Schema = exports.plan30Schema = exports.idealProfileSchema = exports.LEADER_FIXED_ITEMS = exports.PLAN_90_BOXES = exports.PLAN_30_BOXES = exports.IDEAL_PROFILE_BOXES = exports.PICKABLE_WORK_HABITS = exports.WORK_HABIT_KEYS = exports.WORK_HABITS = exports.PLAN_CHECK_LABELS = exports.PLAN_CHECKS = exports.MUST_HAVE_CHECK_LABELS = exports.MUST_HAVE_CHECKS = exports.TOOL_LEVEL_LABELS = exports.TOOL_LEVELS = exports.TOOL_FREQUENCY_LABELS = exports.TOOL_FREQUENCIES = void 0;
exports.workHabitOf = workHabitOf;
exports.emptyIdealProfile = emptyIdealProfile;
exports.emptyPlan30 = emptyPlan30;
exports.emptyPlan90 = emptyPlan90;
exports.idealProfileOf = idealProfileOf;
exports.plan30Of = plan30Of;
exports.plan90Of = plan90Of;
exports.rowStateOf = rowStateOf;
exports.withoutEmptyRows = withoutEmptyRows;
exports.cleanIdealProfile = cleanIdealProfile;
exports.cleanPlan30 = cleanPlan30;
exports.cleanPlan90 = cleanPlan90;
exports.progressShare = progressShare;
exports.idealProfileProgress = idealProfileProgress;
exports.plan30Progress = plan30Progress;
exports.plan90Progress = plan90Progress;
exports.idealProfileSections = idealProfileSections;
exports.plan30Sections = plan30Sections;
exports.plan90Sections = plan90Sections;
exports.sectionsText = sectionsText;
exports.hasIdealProfile = hasIdealProfile;
const zod_1 = require("zod");
// ADR 0083 (owner feedback 2026-11-01 Q4–Q8, the four templates in `Docs/Feedback/Template/`): the Ideal candidate profile,
// the 30-day plan, the 90-day plan and the Leadership add-on, as data. The templates are the spec: each box's number, question,
// min and max, which rows are required (*), the fixed choices and the grey "e.g." under each box live here, so the request
// form, the hire's plans page, the server's checks and the text Jev reads can never disagree.
//
// Rules shared by every box:
// - A row counts once every cell in it is filled. A half-filled row is a problem until it is finished or cleared.
// - "min" rows must be filled (the template's * rows); the form never offers more than "max".
// - Nothing is refused on save for being incomplete: a draft may be half done. Completeness is checked where it matters
//   (the tab's green tick, the hire's joining).
// - The Leadership add-on's parts count only while the role manages people. Switching that off hides them; it deletes nothing.
// --- The fixed choices ------------------------------------------------------------------------------------------------
exports.TOOL_FREQUENCIES = ['daily', 'weekly', 'occasionally'];
exports.TOOL_FREQUENCY_LABELS = { daily: 'Daily', weekly: 'Weekly', occasionally: 'Occasionally' };
exports.TOOL_LEVELS = ['basic', 'working', 'advanced'];
exports.TOOL_LEVEL_LABELS = { basic: 'Basic', working: 'Working', advanced: 'Advanced' };
exports.MUST_HAVE_CHECKS = ['cv', 'test', 'interview'];
exports.MUST_HAVE_CHECK_LABELS = { cv: 'CV', test: 'Test', interview: 'Interview' };
exports.PLAN_CHECKS = ['live_demo', 'quiz', 'test_task'];
exports.PLAN_CHECK_LABELS = { live_demo: 'Live demo', quiz: 'Quiz', test_task: 'Test task' };
/**
 * The template's work habit list (box 7), each with its standard interview question. Fixed for now (owner 2026-11-01 Q4, default
 * taken): HR may get an editable list later. The two leadership habits are never picked in box 7: on a role that manages people the
 * Leadership add-on (1.3) adds both automatically, each with its own "why it matters".
 */
exports.WORK_HABITS = [
    { key: 'owns_problems', label: 'Owns problems', question: 'Tell me about a problem you fixed that wasn’t your job.', isLeadership: false },
    { key: 'clear_updates', label: 'Writes clear short updates', question: 'Write the update you’d send your manager about this situation. (2 minutes)', isLeadership: false },
    { key: 'asks_early', label: 'Asks early when stuck', question: 'When did you last get stuck? How long before you asked for help?', isLeadership: false },
    { key: 'keeps_deadlines', label: 'Keeps deadlines', question: 'Tell me about a deadline you missed. What did you do?', isLeadership: false },
    { key: 'accurate_detail', label: 'Accurate with detail', question: 'Spot the 3 errors in this document.', isLeadership: false },
    { key: 'calm_clients', label: 'Calm with upset clients', question: 'Role-play: an angry client chasing a late job.', isLeadership: false },
    { key: 'agreed_hours', label: 'Works the agreed hours', question: 'Our hours are [shift]. What would make that hard for you?', isLeadership: false },
    { key: 'learns_tools', label: 'Learns tools fast', question: 'Here’s a tool you haven’t used. Do this task in 10 minutes.', isLeadership: false },
    { key: 'develops_others', label: 'Develops others', question: 'Tell me about someone you made better at their job.', isLeadership: true },
    { key: 'decides_without_facts', label: 'Decides without all the facts', question: 'Tell me about the last decision you made without all the facts.', isLeadership: true },
];
exports.WORK_HABIT_KEYS = exports.WORK_HABITS.map((habit) => habit.key);
function workHabitOf(key) {
    const habit = exports.WORK_HABITS.find((candidate) => candidate.key === key);
    if (!habit)
        throw new Error(`Unknown work habit ${key}`);
    return habit;
}
/** The habits box 7 offers: the eight standard ones. The leadership two arrive through the add-on (1.3). */
exports.PICKABLE_WORK_HABITS = exports.WORK_HABITS.filter((habit) => !habit.isLeadership);
function box(number, title, min, max, rule, example, note = null) {
    return { number, title, min, max, rule, note, example };
}
exports.IDEAL_PROFILE_BOXES = {
    tools: box('1', 'Which tools and systems will they use?', 1, 6, 'min 1, max 6', 'Google Sheets · Daily · Advanced · Yes', 'This decides what the test covers. Every row marked Test = Yes is assessed before interview.'),
    mustHaves: box('2', 'What must they already be able to do on day one?', 3, 5, 'min 3, max 5', 'Writes a clear client email · Test'),
    niceToHaves: box('3', 'What would make them stand out, but you could teach?', 0, 5, 'min 0, max 5', 'Has used Property Meld or AppFolio'),
    experience: box('4', 'What experience do they need?', 1, 1, 'all required', '1 to 3 years · customer support or account management · BPO serving US or UK clients'),
    qualifications: box('5', 'Required qualifications, certificates or languages', 0, 3, 'min 0, max 3', 'Fluent spoken and written English', 'Only list what the job cannot be done without.'),
    dealBreakers: box('6', 'What rules someone out immediately?', 0, 3, 'min 0, max 3', 'Cannot work the US night shift', 'Hours, location or legal conditions only.'),
    workHabits: box('7', 'Which work habits matter most?', 3, 5, 'min 3, max 5', 'Owns problems · No one to escalate to at 2 a.m. Bangladesh time', 'Pick from the habit list. Each habit carries a standard interview question.'),
    redFlags: box('8', 'What went wrong with people who did badly in this or a similar role?', 0, 3, 'min 0, max 3', 'Promised clients times they could not keep', 'Behaviours, not personal traits.'),
    leadExperience: box('1.1', 'What people-management experience do they need?', 1, 1, 'all required', '3 or more · 2 years · client-facing operations or support team'),
    leadSituation: box('1.2', 'What leadership situation will they face first?', 1, 1, 'exactly 1', 'A team member keeps missing response times · names the gap with numbers, agrees a fix and a date, follows up, escalates if it repeats', 'It becomes the interview scenario.'),
    leadHabits: box('1.3', 'Leadership habits', 1, 1, 'both required', '', 'Added to box 7 automatically. Write why each matters in this role.'),
};
exports.PLAN_30_BOXES = {
    skills: box('1', 'What must they know or be able to do by Day 30?', 2, 4, 'min 2, max 4', 'Walk through the work order lifecycle in Ravndor · Day 10 · Live demo · Arif'),
    delivered: box('2', 'What real work will they have delivered by Day 30?', 1, 3, 'min 1, max 3', 'Work orders taken from intake to vendor assigned · 40 · 2 or fewer errors · Ravndor'),
    rampUp: box('3', 'Which number shows they are ramping up?', 1, 1, 'exactly 1', 'First response to new work orders · within 2 business hours · within 1 business hour · Ravndor', 'Use one of the numbers from the 90-day plan, box 2.'),
    meet: box('4', 'Who must they have met or started working with by Day 30?', 0, 5, 'min 0, max 5', 'AMH coordinators · they send a third of our work orders', 'Their own team is added automatically.'),
    support: box('5', 'How will you support them in the first 30 days?', 1, 1, 'all required', 'Nuammer in week 1 · 30 minutes twice a week · any job over the not-to-exceed limit'),
    leadTeam: box('2.1', 'Who will they manage?', 1, 1, 'all required', '4 · client success executives'),
    leadFixed: box('2.2', 'Fixed Day 30 items for every leader', 1, 1, 'set the dates only', 'Day 10 · Day 25 · Day 30'),
    leadProblem: box('2.3', 'What is the team’s biggest problem right now?', 1, 1, '1 to 2 sentences', 'Executives escalate every vendor decision instead of making it themselves'),
};
exports.PLAN_90_BOXES = {
    owns: box('1', 'What will they own alone by Day 90, without your sign-off?', 1, 3, 'min 1, max 3', 'Two client accounts day to day, including approving jobs under the not-to-exceed limit'),
    numbers: box('2', 'Which numbers will you judge them on at Day 90?', 2, 4, 'min 2, max 4', 'First response to new work orders · within 1 business hour on 90% · Ravndor · Arif', 'Measured over the last 4 weeks before Day 90.'),
    improvement: box('3', 'What one thing will they have improved or fixed by Day 90?', 1, 1, 'exactly 1 for a role that manages people; optional otherwise', 'Vendor quotes chased by phone · every quote request tracked in Ravndor with a 24-hour follow-up · Day 75'),
    warningSigns: box('4', 'What would tell you at Day 90 that this was the wrong hire?', 1, 3, 'min 1, max 3', 'Still asks for sign-off on routine jobs', 'Things you could see or count.'),
    greatHire: box('5', 'What would make this a clearly great hire in 12 months?', 1, 3, 'min 1, max 3', 'Runs 5 accounts alone and has trained the next hire on intake'),
    leadMeasures: box('3.1', 'Where is the team today, and where must it be by Day 90?', 2, 3, 'min 2, max 3', 'Team first response · 3 hours average · under 1 hour · Ravndor', 'These sit beside the leader’s own numbers in box 2. Both count at the Day 90 review.'),
    leadInPlace: box('3.2', 'What must be in place in the team by Day 90?', 1, 3, 'min 1, max 3', 'Every team member has their own 90-day plan'),
};
/** The add-on's three fixed Day 30 items (2.2): only their dates are set. */
exports.LEADER_FIXED_ITEMS = [
    { key: 'oneToOnesBy', label: '1:1 with every team member' },
    { key: 'teamReportBy', label: 'Written report on the team today: what works, what doesn’t, top 3 fixes' },
    { key: 'targetsAgreedBy', label: 'Team targets for Day 90 agreed with you (box 3.1 of the 90-day plan)' },
];
// --- Schemas -----------------------------------------------------------------------------------------------------------
const CELL_MAX = 300;
const LONG_MAX = 1000;
const cell = (max = CELL_MAX) => zod_1.z.string().trim().max(max, `Keep it under ${max} characters.`).default('');
const choice = (values) => zod_1.z.enum(values).nullable().default(null);
/** A day number. Out-of-range days are saved and called out as a problem, so a draft is never refused. */
const dayNumber = zod_1.z.number().int('Use a whole day number.').min(0).max(365).nullable().default(null);
const count = zod_1.z.number().int('Use a whole number.').min(0).max(999).nullable().default(null);
const textRowSchema = zod_1.z.object({ text: cell() });
const toolRowSchema = zod_1.z.object({ tool: cell(120), frequency: choice(exports.TOOL_FREQUENCIES), level: choice(exports.TOOL_LEVELS), test: zod_1.z.boolean().nullable().default(null) });
const mustHaveRowSchema = zod_1.z.object({ skill: cell(), check: choice(exports.MUST_HAVE_CHECKS) });
const habitRowSchema = zod_1.z.object({ habit: zod_1.z.enum(exports.WORK_HABIT_KEYS).nullable().default(null), why: cell() });
const experienceSchema = zod_1.z.object({ yearsMin: count, yearsMax: count, workType: cell(), companyType: cell() });
const profileLeadershipSchema = zod_1.z.object({
    mostManaged: count,
    yearsManaging: count,
    teamType: cell(),
    situation: cell(LONG_MAX),
    strongAnswer: cell(LONG_MAX),
    developsOthersWhy: cell(),
    decidesWhy: cell(),
});
exports.idealProfileSchema = zod_1.z.object({
    tools: zod_1.z.array(toolRowSchema).max(exports.IDEAL_PROFILE_BOXES.tools.max).default([]),
    mustHaves: zod_1.z.array(mustHaveRowSchema).max(exports.IDEAL_PROFILE_BOXES.mustHaves.max).default([]),
    niceToHaves: zod_1.z.array(textRowSchema).max(exports.IDEAL_PROFILE_BOXES.niceToHaves.max).default([]),
    experience: experienceSchema.default(() => experienceSchema.parse({})),
    qualifications: zod_1.z.array(textRowSchema).max(exports.IDEAL_PROFILE_BOXES.qualifications.max).default([]),
    dealBreakers: zod_1.z.array(textRowSchema).max(exports.IDEAL_PROFILE_BOXES.dealBreakers.max).default([]),
    workHabits: zod_1.z.array(habitRowSchema).max(exports.IDEAL_PROFILE_BOXES.workHabits.max).default([]),
    redFlags: zod_1.z.array(textRowSchema).max(exports.IDEAL_PROFILE_BOXES.redFlags.max).default([]),
    leadership: profileLeadershipSchema.default(() => profileLeadershipSchema.parse({})),
});
const skillRowSchema = zod_1.z.object({ canDo: cell(), byDay: dayNumber, check: choice(exports.PLAN_CHECKS), checkedBy: cell(120) });
const deliveredRowSchema = zod_1.z.object({ work: cell(), howMuch: cell(120), qualityBar: cell(), whereChecked: cell(120) });
const meetRowSchema = zod_1.z.object({ who: cell(160), why: cell() });
const rampUpSchema = zod_1.z.object({ measure: cell(), day30Target: cell(), day90Target: cell(), whereTracked: cell(120) });
const supportSchema = zod_1.z.object({ trainer: cell(160), meetingRhythm: cell(), comeToYouFor: cell() });
const plan30LeadershipSchema = zod_1.z.object({ teamSize: count, teamRoles: cell(), oneToOnesBy: dayNumber, teamReportBy: dayNumber, targetsAgreedBy: dayNumber, biggestProblem: cell(600) });
exports.plan30Schema = zod_1.z.object({
    skills: zod_1.z.array(skillRowSchema).max(exports.PLAN_30_BOXES.skills.max).default([]),
    delivered: zod_1.z.array(deliveredRowSchema).max(exports.PLAN_30_BOXES.delivered.max).default([]),
    rampUp: rampUpSchema.default(() => rampUpSchema.parse({})),
    meet: zod_1.z.array(meetRowSchema).max(exports.PLAN_30_BOXES.meet.max).default([]),
    support: supportSchema.default(() => supportSchema.parse({})),
    leadership: plan30LeadershipSchema.default(() => plan30LeadershipSchema.parse({})),
});
const numberRowSchema = zod_1.z.object({ measure: cell(), target: cell(), whereTracked: cell(120), checkedBy: cell(120) });
const improvementSchema = zod_1.z.object({ problem: cell(), doneLooksLike: cell(), byDay: dayNumber });
const teamMeasureRowSchema = zod_1.z.object({ measure: cell(), today: cell(), day90Target: cell(), whereTracked: cell(120) });
const plan90LeadershipSchema = zod_1.z.object({
    teamMeasures: zod_1.z.array(teamMeasureRowSchema).max(exports.PLAN_90_BOXES.leadMeasures.max).default([]),
    inPlace: zod_1.z.array(textRowSchema).max(exports.PLAN_90_BOXES.leadInPlace.max).default([]),
});
exports.plan90Schema = zod_1.z.object({
    owns: zod_1.z.array(textRowSchema).max(exports.PLAN_90_BOXES.owns.max).default([]),
    numbers: zod_1.z.array(numberRowSchema).max(exports.PLAN_90_BOXES.numbers.max).default([]),
    improvement: improvementSchema.default(() => improvementSchema.parse({})),
    warningSigns: zod_1.z.array(textRowSchema).max(exports.PLAN_90_BOXES.warningSigns.max).default([]),
    greatHire: zod_1.z.array(textRowSchema).max(exports.PLAN_90_BOXES.greatHire.max).default([]),
    leadership: plan90LeadershipSchema.default(() => plan90LeadershipSchema.parse({})),
});
function emptyIdealProfile() {
    return exports.idealProfileSchema.parse({});
}
function emptyPlan30() {
    return exports.plan30Schema.parse({});
}
function emptyPlan90() {
    return exports.plan90Schema.parse({});
}
/** Whatever was stored, as a full profile (anything unreadable is empty, never an error). */
function idealProfileOf(stored) {
    const parsed = exports.idealProfileSchema.safeParse(stored ?? {});
    return parsed.success ? parsed.data : emptyIdealProfile();
}
function plan30Of(stored) {
    const parsed = exports.plan30Schema.safeParse(stored ?? {});
    return parsed.success ? parsed.data : emptyPlan30();
}
function plan90Of(stored) {
    const parsed = exports.plan90Schema.safeParse(stored ?? {});
    return parsed.success ? parsed.data : emptyPlan90();
}
function isBlank(value) {
    return value === null || (typeof value === 'string' && value.trim() === '');
}
function rowStateOf(row) {
    const values = Object.values(row);
    const blanks = values.filter(isBlank).length;
    if (blanks === values.length)
        return 'empty';
    return blanks === 0 ? 'full' : 'partial';
}
/** Fully empty rows go before saving, so what is stored is what was written; the form pads back to the box's minimum. */
function withoutEmptyRows(rows) {
    return rows.filter((row) => rowStateOf(row) !== 'empty');
}
function cleanIdealProfile(profile) {
    return {
        ...profile,
        tools: withoutEmptyRows(profile.tools),
        mustHaves: withoutEmptyRows(profile.mustHaves),
        niceToHaves: withoutEmptyRows(profile.niceToHaves),
        qualifications: withoutEmptyRows(profile.qualifications),
        dealBreakers: withoutEmptyRows(profile.dealBreakers),
        workHabits: withoutEmptyRows(profile.workHabits),
        redFlags: withoutEmptyRows(profile.redFlags),
    };
}
function cleanPlan30(plan) {
    return { ...plan, skills: withoutEmptyRows(plan.skills), delivered: withoutEmptyRows(plan.delivered), meet: withoutEmptyRows(plan.meet) };
}
function cleanPlan90(plan) {
    return {
        ...plan,
        owns: withoutEmptyRows(plan.owns),
        numbers: withoutEmptyRows(plan.numbers),
        warningSigns: withoutEmptyRows(plan.warningSigns),
        greatHire: withoutEmptyRows(plan.greatHire),
        leadership: { teamMeasures: withoutEmptyRows(plan.leadership.teamMeasures), inPlace: withoutEmptyRows(plan.leadership.inPlace) },
    };
}
function label(boxes, key) {
    return `Box ${boxes[key]?.number ?? key}`;
}
function listCheck(boxes, key, rows, min) {
    const full = rows.filter((row) => rowStateOf(row) === 'full').length;
    const isHalf = rows.some((row) => rowStateOf(row) === 'partial');
    const name = label(boxes, key);
    let problem = null;
    if (isHalf)
        problem = `${name}: finish the half-filled row, or clear it.`;
    else if (full < min)
        problem = min === 1 ? `${name}: fill in at least one row.` : `${name}: fill in at least ${min} rows (${full} so far).`;
    return { box: key, isRequired: min > 0, problem };
}
function fieldsCheck(boxes, key, row, isRequired) {
    const state = rowStateOf(row);
    const name = label(boxes, key);
    if (state === 'full' || (!isRequired && state === 'empty'))
        return { box: key, isRequired, problem: null };
    return { box: key, isRequired, problem: isRequired && state === 'empty' ? `${name}: fill in every field.` : `${name}: finish every field, or clear them.` };
}
function dayCheck(boxes, key, days, last) {
    return days.some((day) => day !== null && (day < 1 || day > last)) ? `${label(boxes, key)}: use a day from Day 1 to Day ${last}.` : null;
}
function progressOf(checks) {
    const required = checks.filter((check) => check.isRequired);
    const problems = checks.flatMap((check) => (check.problem === null ? [] : [{ box: check.box, message: check.problem }]));
    return { done: required.filter((check) => check.problem === null).length, total: required.length, problems, isComplete: problems.length === 0 };
}
/** 0 to 1, for "How complete": the required boxes done. */
function progressShare(progress) {
    if (progress.total === 0)
        return progress.isComplete ? 1 : 0;
    return progress.isComplete ? 1 : Math.min(progress.done / progress.total, 0.99);
}
function idealProfileProgress(profile, managesPeople) {
    const boxes = exports.IDEAL_PROFILE_BOXES;
    const { experience, leadership } = profile;
    const years = experience.yearsMin !== null && experience.yearsMax !== null && experience.yearsMin > experience.yearsMax ? 'Box 4: the fewest years can’t be more than the most.' : null;
    const habits = profile.workHabits.map((row) => row.habit).filter((habit) => habit !== null);
    const twice = new Set(habits).size !== habits.length ? 'Box 7: each habit once.' : null;
    const checks = [
        listCheck(boxes, 'tools', profile.tools, boxes.tools.min),
        listCheck(boxes, 'mustHaves', profile.mustHaves, boxes.mustHaves.min),
        listCheck(boxes, 'niceToHaves', profile.niceToHaves, boxes.niceToHaves.min),
        (() => {
            const check = fieldsCheck(boxes, 'experience', experience, true);
            return check.problem === null && years !== null ? { ...check, problem: years } : check;
        })(),
        listCheck(boxes, 'qualifications', profile.qualifications, boxes.qualifications.min),
        listCheck(boxes, 'dealBreakers', profile.dealBreakers, boxes.dealBreakers.min),
        (() => {
            const check = listCheck(boxes, 'workHabits', profile.workHabits, boxes.workHabits.min);
            return check.problem === null && twice !== null ? { ...check, problem: twice } : check;
        })(),
        listCheck(boxes, 'redFlags', profile.redFlags, boxes.redFlags.min),
    ];
    if (managesPeople) {
        checks.push(fieldsCheck(boxes, 'leadExperience', { mostManaged: leadership.mostManaged, yearsManaging: leadership.yearsManaging, teamType: leadership.teamType }, true), fieldsCheck(boxes, 'leadSituation', { situation: leadership.situation, strongAnswer: leadership.strongAnswer }, true), fieldsCheck(boxes, 'leadHabits', { developsOthersWhy: leadership.developsOthersWhy, decidesWhy: leadership.decidesWhy }, true));
    }
    return progressOf(checks);
}
function plan30Progress(plan, managesPeople) {
    const boxes = exports.PLAN_30_BOXES;
    const skills = listCheck(boxes, 'skills', plan.skills, boxes.skills.min);
    const skillDays = dayCheck(boxes, 'skills', plan.skills.map((row) => row.byDay), 30);
    const checks = [
        skills.problem === null && skillDays !== null ? { ...skills, problem: skillDays } : skills,
        listCheck(boxes, 'delivered', plan.delivered, boxes.delivered.min),
        fieldsCheck(boxes, 'rampUp', plan.rampUp, true),
        listCheck(boxes, 'meet', plan.meet, boxes.meet.min),
        fieldsCheck(boxes, 'support', plan.support, true),
    ];
    if (managesPeople) {
        const lead = plan.leadership;
        const fixed = fieldsCheck(boxes, 'leadFixed', { oneToOnesBy: lead.oneToOnesBy, teamReportBy: lead.teamReportBy, targetsAgreedBy: lead.targetsAgreedBy }, true);
        const fixedDays = dayCheck(boxes, 'leadFixed', [lead.oneToOnesBy, lead.teamReportBy, lead.targetsAgreedBy], 30);
        checks.push(fieldsCheck(boxes, 'leadTeam', { teamSize: lead.teamSize, teamRoles: lead.teamRoles }, true), fixed.problem === null && fixedDays !== null ? { ...fixed, problem: fixedDays } : fixed, fieldsCheck(boxes, 'leadProblem', { biggestProblem: lead.biggestProblem }, true));
    }
    return progressOf(checks);
}
/**
 * 90-day box 3 is "exactly 1 for Senior and Lead; optional for Executive". Nothing records seniority, so it is required on a role
 * that manages people and optional otherwise (ADR 0083); a half-filled one is always a problem.
 */
function plan90Progress(plan, managesPeople) {
    const boxes = exports.PLAN_90_BOXES;
    const improvement = fieldsCheck(boxes, 'improvement', plan.improvement, managesPeople);
    const improvementDay = dayCheck(boxes, 'improvement', [plan.improvement.byDay], 90);
    const checks = [
        listCheck(boxes, 'owns', plan.owns, boxes.owns.min),
        listCheck(boxes, 'numbers', plan.numbers, boxes.numbers.min),
        improvement.problem === null && improvementDay !== null ? { ...improvement, problem: improvementDay } : improvement,
        listCheck(boxes, 'warningSigns', plan.warningSigns, boxes.warningSigns.min),
        listCheck(boxes, 'greatHire', plan.greatHire, boxes.greatHire.min),
    ];
    if (managesPeople) {
        checks.push(listCheck(boxes, 'leadMeasures', plan.leadership.teamMeasures, boxes.leadMeasures.min), listCheck(boxes, 'leadInPlace', plan.leadership.inPlace, boxes.leadInPlace.min));
    }
    return progressOf(checks);
}
const SEP = ' · ';
function joined(cells) {
    return cells.filter((part) => part !== null && part.trim() !== '').map((part) => part.trim()).join(SEP);
}
function day(value) {
    return value === null ? null : `Day ${value}`;
}
function numberText(value, unit) {
    return value === null ? null : `${value} ${unit}`;
}
function section(boxes, key, lines) {
    const meta = boxes[key];
    return { box: key, number: meta?.number ?? key, title: meta?.title ?? key, lines: lines.filter((line) => line !== '') };
}
function textLines(rows) {
    return rows.map((row) => row.text.trim());
}
/** Every box with something in it, in the template's order. The add-on's Part 1 only while the role manages people. */
function idealProfileSections(profile, managesPeople) {
    const boxes = exports.IDEAL_PROFILE_BOXES;
    const { experience: ex, leadership: lead } = profile;
    const years = ex.yearsMin === null && ex.yearsMax === null ? null : ex.yearsMin !== null && ex.yearsMax !== null ? `${ex.yearsMin} to ${ex.yearsMax} years` : `${ex.yearsMin ?? ex.yearsMax} years`;
    const habits = profile.workHabits.map((row) => joined([row.habit === null ? null : workHabitOf(row.habit).label, row.why]));
    const leaderHabits = managesPeople
        ? [joined([lead.developsOthersWhy === '' ? null : workHabitOf('develops_others').label, lead.developsOthersWhy]), joined([lead.decidesWhy === '' ? null : workHabitOf('decides_without_facts').label, lead.decidesWhy])]
        : [];
    const sections = [
        section(boxes, 'tools', profile.tools.map((row) => joined([row.tool, row.frequency && exports.TOOL_FREQUENCY_LABELS[row.frequency], row.level && exports.TOOL_LEVEL_LABELS[row.level], row.test === null ? null : row.test ? 'Test it: yes' : 'Test it: no']))),
        section(boxes, 'mustHaves', profile.mustHaves.map((row) => joined([row.skill, row.check && `checked by ${exports.MUST_HAVE_CHECK_LABELS[row.check]}`]))),
        section(boxes, 'niceToHaves', textLines(profile.niceToHaves)),
        section(boxes, 'experience', [joined([years, ex.workType, ex.companyType])]),
        section(boxes, 'qualifications', textLines(profile.qualifications)),
        section(boxes, 'dealBreakers', textLines(profile.dealBreakers)),
        section(boxes, 'workHabits', [...habits, ...leaderHabits]),
        section(boxes, 'redFlags', textLines(profile.redFlags)),
    ];
    if (managesPeople) {
        sections.push(section(boxes, 'leadExperience', [joined([numberText(lead.mostManaged, 'or more people managed'), numberText(lead.yearsManaging, 'years managing people'), lead.teamType])]), section(boxes, 'leadSituation', [joined([lead.situation, lead.strongAnswer === '' ? null : `a strong answer: ${lead.strongAnswer}`])]));
    }
    return sections.filter((part) => part.lines.length > 0);
}
function plan30Sections(plan, managesPeople) {
    const boxes = exports.PLAN_30_BOXES;
    const lead = plan.leadership;
    const sections = [
        section(boxes, 'skills', plan.skills.map((row) => joined([row.canDo, day(row.byDay), row.check && exports.PLAN_CHECK_LABELS[row.check], row.checkedBy]))),
        section(boxes, 'delivered', plan.delivered.map((row) => joined([row.work, row.howMuch, row.qualityBar, row.whereChecked]))),
        section(boxes, 'rampUp', [joined([plan.rampUp.measure, plan.rampUp.day30Target && `Day 30: ${plan.rampUp.day30Target}`, plan.rampUp.day90Target && `Day 90: ${plan.rampUp.day90Target}`, plan.rampUp.whereTracked])]),
        section(boxes, 'meet', plan.meet.map((row) => joined([row.who, row.why]))),
        section(boxes, 'support', [joined([plan.support.trainer && `Trained by ${plan.support.trainer}`, plan.support.meetingRhythm, plan.support.comeToYouFor && `Come to you for: ${plan.support.comeToYouFor}`])]),
    ];
    if (managesPeople) {
        sections.push(section(boxes, 'leadTeam', [joined([numberText(lead.teamSize, 'people'), lead.teamRoles])]), section(boxes, 'leadFixed', exports.LEADER_FIXED_ITEMS.map((item) => (lead[item.key] === null ? '' : joined([item.label, day(lead[item.key])])))), section(boxes, 'leadProblem', [lead.biggestProblem.trim()]));
    }
    return sections.filter((part) => part.lines.length > 0);
}
function plan90Sections(plan, managesPeople) {
    const boxes = exports.PLAN_90_BOXES;
    const sections = [
        section(boxes, 'owns', textLines(plan.owns)),
        section(boxes, 'numbers', plan.numbers.map((row) => joined([row.measure, row.target, row.whereTracked, row.checkedBy]))),
        section(boxes, 'improvement', [joined([plan.improvement.problem, plan.improvement.doneLooksLike, day(plan.improvement.byDay)])]),
        section(boxes, 'warningSigns', textLines(plan.warningSigns)),
        section(boxes, 'greatHire', textLines(plan.greatHire)),
    ];
    if (managesPeople) {
        sections.push(section(boxes, 'leadMeasures', plan.leadership.teamMeasures.map((row) => joined([row.measure, row.today && `today ${row.today}`, row.day90Target && `Day 90: ${row.day90Target}`, row.whereTracked]))), section(boxes, 'leadInPlace', textLines(plan.leadership.inPlace)));
    }
    return sections.filter((part) => part.lines.length > 0);
}
/** The sections as one block, a heading per box: how a part reads in "Edited by HR" and in the change list. */
function sectionsText(sections) {
    return sections.map((part) => `${part.number}. ${part.title}\n${part.lines.map((line) => `• ${line}`).join('\n')}`).join('\n\n');
}
function hasIdealProfile(profile, managesPeople) {
    return idealProfileSections(profile, managesPeople).length > 0;
}
//# sourceMappingURL=hiring-templates.js.map